<script>
  import { tick } from 'svelte';
  import { _ } from 'svelte-i18n';
  import Fa from 'svelte-fa';
  import { faLock } from '@fortawesome/free-solid-svg-icons';
  import Ribbon from './Ribbon.svelte';
  import { now } from './battlepass/now.js';
  import { deriveShopTabs } from './shop-tabs.js';
  import { dragScroll } from './actions/dragScroll.js';
  import NotificationDot from './NotificationDot.svelte';
  import { deriveTabInterest } from './tab-interest.js';
  import { seenVersion, readSeenFlags } from './tab-seen.js';
  import {
    storeSection,
    storePacks,
    adventCalendarEvent,
    battlePassEvent,
    rechargeRewardsEvent,
    luckyDrawEvent,
    subscriptions,
    vipLevel,
    castleLevel,
    growthFundConfig,
    monsterGrowthFundConfig,
    claimedGrowthFundPrizes,
    claimedMonsterGrowthFundPrizes,
    currentAdventClaimed,
    playerGrowthFund,
    playerMonsterGrowthFund,
    defeatedMonsterLevels,
    userMenuOpen,
    chainPromoEvent,
  } from '../stores.js';

  let stripEl;

  let activeStorePacks = $derived.by(() => {
    const ts = Date.now();
    return ($storePacks || []).filter((p) => !p.endAt || new Date(p.endAt).getTime() > ts);
  });

  let tabs = $derived(
    deriveShopTabs({
      activeStorePacks,
      adventCalendarEvent: $adventCalendarEvent,
      battlePassEvent: $battlePassEvent,
      rechargeRewardsEvent: $rechargeRewardsEvent,
      luckyDrawEvent: $luckyDrawEvent,
      chainPromoEvent: $chainPromoEvent,
      subscriptions: $subscriptions,
      vipLevel: $vipLevel,
      castleLevel: $castleLevel,
      growthFundConfig: $growthFundConfig,
      monsterGrowthFundConfig: $monsterGrowthFundConfig,
      claimedGrowthFundPrizes: $claimedGrowthFundPrizes,
      claimedMonsterGrowthFundPrizes: $claimedMonsterGrowthFundPrizes,
      now: $now,
      seen: {
        adventCalendarSeen: localStorage.getItem('adventCalendarSeen'),
        webBattlePassSeen: localStorage.getItem('webBattlePassSeen'),
      },
    })
  );

  // NOTE: `$seenVersion` is read deliberately (not dead code). readSeenFlags()
  // reads localStorage non-reactively, so this bare dependency is what makes the
  // map recompute after a sub-tab's markSeenFlag() bumps the version — keep it.
  let interest = $derived(
    ($seenVersion, deriveTabInterest({
      activeStorePacks,
      subscriptions: $subscriptions,
      adventCalendarEvent: $adventCalendarEvent,
      currentAdventClaimed: $currentAdventClaimed,
      rechargeRewardsEvent: $rechargeRewardsEvent,
      battlePassEvent: $battlePassEvent,
      growthFundConfig: $growthFundConfig,
      claimedGrowthFundPrizes: $claimedGrowthFundPrizes,
      playerGrowthFund: $playerGrowthFund,
      castleLevel: $castleLevel,
      monsterGrowthFundConfig: $monsterGrowthFundConfig,
      claimedMonsterGrowthFundPrizes: $claimedMonsterGrowthFundPrizes,
      playerMonsterGrowthFund: $playerMonsterGrowthFund,
      defeatedMonsterLevels: $defeatedMonsterLevels,
      chainPromoEvent: $chainPromoEvent,
      seen: readSeenFlags(),
      now: $now,
    }))
  );

  function selectTab(key) {
    storeSection.set(key);
  }

  // Keep the active pill in view when the section changes.
  $effect(() => {
    $storeSection;
    tick().then(() => {
      const active = stripEl?.querySelector('.mtab.active');
      active?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    });
  });

  // Re-center the active pill within the region NOT covered by the overlaid user
  // controls. Called when the burger toggles: opening widens the controls and
  // pushes the active tab into the shrunken visible area; closing pulls it back.
  // When the menu opens the strip's width shrinks to end where the controls begin
  // (see the .menu-open width rule), so simply centering the active tab keeps it
  // fully within the visible area. Re-center it into the new scrollport.
  function reCenterClearOfMenu() {
    const active = stripEl?.querySelector('.mtab.active');
    active?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }

  let firstMenuRun = true;
  $effect(() => {
    $userMenuOpen;
    if (firstMenuRun) { firstMenuRun = false; return; } // skip the initial mount run
    // Wait for the controls to expand + the spacer to widen (a layout frame past
    // the Svelte DOM flush) so the measurements are final, then re-center.
    tick().then(() => requestAnimationFrame(reCenterClearOfMenu));
  });
</script>

<nav class="mobile-tab-bar" aria-label="Shop sections">
  <ul class="mtab-strip" class:menu-open={$userMenuOpen} bind:this={stripEl} role="tablist" use:dragScroll>
    {#each tabs as tab (tab.key)}
      <li role="presentation">
        <button
          type="button"
          role="tab"
          class="mtab"
          class:active={$storeSection === tab.key}
          aria-selected={$storeSection === tab.key}
          onclick={() => selectTab(tab.key)}
        >
          {#if tab.locked}
            <Fa icon={faLock} fw primaryColor="#7f584b" size="1x" />
          {/if}
          {$_(tab.labelKey, tab.labelParams)}
          {#if tab.isNew}
            <Ribbon>{$_('New')}</Ribbon>
          {/if}
          {#if interest[tab.key]?.dot}
            <NotificationDot kind={interest[tab.key].kind} />
          {/if}
        </button>
      </li>
    {/each}
    <li class="mtab-spacer" role="presentation" aria-hidden="true"></li>
  </ul>
</nav>

<style lang="scss">
  @use 'sass:color';
  @use '../styles/nav-pills.scss' as pills;
  @use '../styles/breakpoints.scss' as bp;
  @use '../styles/vars.scss' as *;

  .mobile-tab-bar {
    width: 100%;
    // Match the submenu strip (`.tabs` in global.scss) so the main menu and
    // sub-tabs share one cohesive background.
    background: color.adjust($dark-color, $lightness: 10%);
  }

  .mtab-strip {
    @include pills.pill-row;
  }

  @if bp.$combine-mobile-header {
    .mtab-strip {
      // A little top room so the pills aren't jammed against the browser/status bar.
      padding-top: 10px;
    }

    // Trailing spacer (a real element — unlike padding-right, its width counts in
    // scrollWidth, so the last pill can scroll clear of the collapsed burger).
    .mtab-spacer {
      flex: 0 0 auto;
      width: 56px;
      align-self: stretch;
      pointer-events: none;
    }

    // When the burger menu expands, shrink the strip so its scrollport ends where
    // the controls begin — the active tab then centers within the visible area and
    // never hides under the menu. The width change animates the push/pull.
    .mtab-strip {
      transition: width 0.24s ease;
    }
    .mtab-strip.menu-open {
      width: calc(100% - 200px);
    }
  }

  .mtab {
    @include pills.pill;
  }

  .mtab.active {
    @include pills.pill-active;
  }
</style>
